/**
 * CorrelationsPage - Página de Correlações Premium
 * Migrado para padrão Premium Fleet
 */

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Checkbox,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import LinkIcon from '@mui/icons-material/Link';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';

import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import { useEffectAsync } from '../reactHelper';
import fetchOrThrow from '../common/util/fetchOrThrow';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import { useTableSelection } from '../common/hooks';

// Componentes extraídos
import CorrelationsTableRow from './components/CorrelationsTableRow';
import {
  PremiumStatCards,
  PremiumEmptyState,
  BulkActionsBar,
  BulkDeleteDialog,
} from '../common/components/premium';

const CorrelationsPage = () => {
  const navigate = useNavigate();
  const t = useTranslation();
  const tokens = usePremiumTokens();

  const [timestamp, setTimestamp] = useState(Date.now());
  const [items, setItems] = useState([]);
  const [devices, setDevices] = useState({});
  const [pois, setPois] = useState({});
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Bulk action states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Fetch correlations, devices and POIs
  useEffectAsync(async () => {
    setLoading(true);
    try {
      const [correlationsResponse, devicesResponse, poisResponse] = await Promise.all([
        fetchOrThrow('/api/correlations'),
        fetchOrThrow('/api/devices'),
        fetchOrThrow('/api/pois'),
      ]);
      const correlationsData = await correlationsResponse.json();
      const devicesData = await devicesResponse.json();
      const poisData = await poisResponse.json();

      setItems(correlationsData);
      setDevices(devicesData.reduce((acc, d) => ({ ...acc, [d.id]: d }), {}));
      setPois(poisData.reduce((acc, p) => ({ ...acc, [p.id]: p }), {}));
    } finally {
      setLoading(false);
    }
  }, [timestamp]);

  // Statistics
  const stats = useMemo(() => {
    const enabled = items.filter((i) => i.enabled).length;
    const disabled = items.filter((i) => !i.enabled).length;
    const intrusive = items.filter((i) => i.intrusive).length;
    return { total: items.length, enabled, disabled, intrusive };
  }, [items]);

  // Cards estatísticos para PremiumStatCards
  const statsCards = [
    {
      key: 'total',
      label: t('sharedAll'),
      value: stats.total,
      color: tokens?.accent?.primary,
      Icon: LinkIcon,
      clickable: true,
      onClick: () => setStatusFilter(''),
      active: statusFilter === '',
    },
    {
      key: 'enabled',
      label: t('correlationEnabled') || 'Habilitadas',
      value: stats.enabled,
      color: tokens?.semantic?.success,
      Icon: CheckCircleIcon,
      clickable: true,
      onClick: () => setStatusFilter('enabled'),
      active: statusFilter === 'enabled',
    },
    {
      key: 'disabled',
      label: t('sharedDisabled'),
      value: stats.disabled,
      color: tokens?.semantic?.error,
      Icon: BlockIcon,
      clickable: true,
      onClick: () => setStatusFilter('disabled'),
      active: statusFilter === 'disabled',
    },
    {
      key: 'intrusive',
      label: t('correlationIntrusive') || 'Intrusivas',
      value: stats.intrusive,
      color: tokens?.semantic?.warning,
      Icon: NotificationsActiveIcon,
      clickable: true,
      onClick: () => setStatusFilter('intrusive'),
      active: statusFilter === 'intrusive',
    },
  ];

  // Filtering + Search
  const filteredItems = useMemo(() => {
    let result = items;
    if (statusFilter === 'enabled') {
      result = result.filter((i) => i.enabled);
    } else if (statusFilter === 'disabled') {
      result = result.filter((i) => !i.enabled);
    } else if (statusFilter === 'intrusive') {
      result = result.filter((i) => i.intrusive);
    }
    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      result = result.filter(
        (i) =>
          i.name?.toLowerCase().includes(lower) ||
          devices[i.deviceId]?.name?.toLowerCase().includes(lower) ||
          devices[i.targetDeviceId]?.name?.toLowerCase().includes(lower) ||
          pois[i.targetPoiId]?.name?.toLowerCase().includes(lower) ||
          i.id?.toString().includes(lower),
      );
    }
    return result;
  }, [items, statusFilter, searchText, devices, pois]);

  const paginatedItems = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredItems.slice(start, start + rowsPerPage);
  }, [filteredItems, page, rowsPerPage]);

  // Bulk selection hook
  const {
    selectedIds,
    selectedCount,
    toggleSelect,
    selectAll,
    clearSelection,
    isSelected,
    isAllSelected,
    isIndeterminate,
    hasSelection,
  } = useTableSelection(filteredItems);

  // Bulk delete handler
  const handleBulkDelete = useCallback(async () => {
    setBulkActionLoading(true);
    try {
      await Promise.all(
        selectedIds.map((id) =>
          fetchOrThrow(`/api/correlations/${id}`, { method: 'DELETE' })
        )
      );
      clearSelection();
      setDeleteDialogOpen(false);
      setTimestamp(Date.now());
    } catch (error) {
      console.error('Erro ao excluir correlações:', error);
    } finally {
      setBulkActionLoading(false);
    }
  }, [selectedIds, clearSelection]);

  // Handlers
  const handleClearFilters = useCallback(() => {
    setSearchText('');
    setStatusFilter('');
    setPage(0);
  }, []);

  const handleRefresh = useCallback(() => {
    setTimestamp(Date.now());
  }, []);

  // Check if has active filters
  const hasFilters = Boolean(searchText.trim() || statusFilter);

  // Header styles for premium table
  const headerCellSx = {
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: tokens?.text?.secondary,
    borderBottom: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
    py: 1.5,
  };

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'sharedCorrelations']}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Loading State */}
          {loading && items.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <Typography variant="body1" sx={{ color: tokens?.text?.secondary }}>
                {t('sharedLoading') || 'Carregando...'}
              </Typography>
            </Box>
          ) : (
            <>
              {/* STATISTICS CARDS */}
              <PremiumStatCards cards={statsCards} />

              {/* ACTION BAR */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 1, sm: 1.5 },
                  alignItems: { xs: 'stretch', md: 'center' },
                  flexShrink: 0,
                }}
              >
                <TextField
                  size="small"
                  value={searchText}
                  onChange={(e) => { setSearchText(e.target.value); setPage(0); }}
                  placeholder={t('sharedSearch')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: tokens?.accent?.primary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flex: 1,
                    minWidth: { xs: '100%', sm: 200 },
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: tokens?.surface?.glass,
                      borderRadius: tokens?.radius?.md,
                      '&:hover': { backgroundColor: tokens?.surface?.hover },
                    },
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddCircleIcon />}
                  onClick={() => navigate('/settings/correlation')}
                  sx={{ backgroundColor: tokens?.accent?.primary, '&:hover': { backgroundColor: tokens?.accent?.hover } }}
                >
                  {t('sharedAdd')}
                </Button>
              </Box>

              {/* Results count & Bulk Actions Bar */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                <Typography variant="body2" color="text.secondary">
                  {filteredItems.length} {t('sharedCorrelations') || 'correlações'}
                  {hasFilters && ` (${t('sharedFiltered') || 'filtrado'})`}
                </Typography>

                {hasSelection && (
                  <BulkActionsBar
                    selectedCount={selectedCount}
                    onClearSelection={clearSelection}
                    onDelete={() => setDeleteDialogOpen(true)}
                    loading={bulkActionLoading}
                    entityName={t('sharedCorrelations') || 'correlação(ões)'}
                  />
                )}
              </Box>

              {/* TABLE */}
              <Paper
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  borderRadius: tokens?.radius?.lg || '12px',
                  backgroundColor: tokens?.surface?.glass,
                  border: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
                }}
              >
                <TableContainer className="premium-table-container" sx={{ maxHeight: 600 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" sx={{ ...headerCellSx, width: 48 }}>
                          <Checkbox
                            indeterminate={isIndeterminate}
                            checked={isAllSelected}
                            onChange={selectAll}
                            sx={{
                              color: tokens?.text?.tertiary,
                              '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: tokens?.accent?.primary },
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ ...headerCellSx, width: 48 }} />
                        <TableCell sx={{ ...headerCellSx, width: 60 }}>ID</TableCell>
                        <TableCell sx={headerCellSx}>{t('sharedName')}</TableCell>
                        <TableCell sx={headerCellSx}>{t('correlationDevice') || 'Dispositivo'}</TableCell>
                        <TableCell sx={headerCellSx}>{t('correlationTargetDevice') || 'Alvo'}</TableCell>
                        <TableCell sx={headerCellSx}>{t('correlationRadius') || 'Raio'}</TableCell>
                        <TableCell sx={headerCellSx}>{t('correlationAlertType') || 'Tipo Alerta'}</TableCell>
                        <TableCell sx={headerCellSx}>Status</TableCell>
                        <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>{t('sharedActions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedItems.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} sx={{ border: 0, p: 0 }}>
                            <PremiumEmptyState
                              icon={LinkIcon}
                              title={t('correlationsEmpty') || 'Nenhuma correlação cadastrada'}
                              titleFiltered={t('correlationsNotFound') || 'Nenhuma correlação encontrada'}
                              subtitle={t('correlationsEmptyHint') || 'Comece criando sua primeira correlação'}
                              subtitleFiltered={t('correlationsNotFoundHint') || 'Tente ajustar os filtros de busca'}
                              hasFilters={hasFilters}
                              onClearFilters={handleClearFilters}
                              addPath="/settings/correlation"
                              addLabel={t('correlationAdd') || 'Adicionar Correlação'}
                            />
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedItems.map((row) => (
                          <CorrelationsTableRow
                            key={row.id}
                            row={row}
                            devices={devices}
                            pois={pois}
                            onRefresh={handleRefresh}
                            selected={isSelected(row.id)}
                            onSelect={toggleSelect}
                          />
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {filteredItems.length > 0 && (
                  <TablePagination
                    component="div"
                    count={filteredItems.length}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    labelRowsPerPage={t('rowsPerPage') || 'Linhas por página'}
                    sx={{
                      borderTop: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
                      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                        fontSize: '0.8rem',
                        color: tokens?.text?.secondary,
                      },
                    }}
                  />
                )}
              </Paper>
            </>
          )}
        </Box>
      </Box>

      {/* Bulk Delete Dialog */}
      <BulkDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleBulkDelete}
        selectedCount={selectedCount}
        loading={bulkActionLoading}
        entityName={t('sharedCorrelations') || 'correlação(ões)'}
      />
    </PageLayout>
  );
};

export default CorrelationsPage;
